import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, useToast, } from '@cargomatic/ui';
import { cn } from 'utils/ui/classes';
import { useOrderAccessorialRequests, } from './hooks/useOrderAccessorialRequests';
import AccessorialRequestStatusBadge from 'components/v2/AccessorialRequestStatusBadge';
import ShipmentExternalLink from './ShipmentExternalLink';
import CargomaticPane from 'components/v2/pane';
import AccessorialRequestPane from 'components/modules/shipments/AccessorialRequestPane';
import BulkAddAccessorialsModal from 'components/modules/shipments/accounting/BulkAddAccessorialsModal';
import { AlertCircle, ChevronRight } from 'lucide-react';
import MissingEquipmentWarning from './MissingEquipmentWarning';
import formatMoney from 'utils/formatters/Money';
import { NO_DATA } from './constants';
import LegTypeBadge from 'components/v2/LegTypeBadge';
function formatUpdatedAt(time) {
    if (!time)
        return NO_DATA;
    const date = new Date(time);
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}
function titleize(str) {
    if (!str)
        return '';
    return str.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function getShipmentId(req) {
    var _a, _b;
    return typeof req.shipment === 'string'
        ? req.shipment
        : ((_b = (_a = req.shipment) === null || _a === void 0 ? void 0 : _a._id) !== null && _b !== void 0 ? _b : '');
}
function getShipmentDisplayId(req) {
    var _a;
    return req.shipment != null && typeof req.shipment === 'object'
        ? ((_a = req.shipment.shipment_id) !== null && _a !== void 0 ? _a : '')
        : '';
}
function getShipperCost(req) {
    var _a, _b;
    return ((_a = req.shipper_rate) !== null && _a !== void 0 ? _a : 0) * ((_b = req.shipper_unit_qty) !== null && _b !== void 0 ? _b : 1);
}
function getCarrierCost(req) {
    var _a, _b;
    return ((_a = req.carrier_rate) !== null && _a !== void 0 ? _a : 0) * ((_b = req.carrier_unit_qty) !== null && _b !== void 0 ? _b : 1);
}
function getPayeeName(req) {
    var _a, _b;
    return req.payee_type === 'carrier'
        ? ((_a = req.carrier_name) !== null && _a !== void 0 ? _a : '')
        : ((_b = req.vendor_name) !== null && _b !== void 0 ? _b : '');
}
function getActionRequired(req) {
    var _a;
    const val = (_a = req.action_required) !== null && _a !== void 0 ? _a : '';
    return ['declined', 'approved', 'accepted'].includes(val) ? '' : val;
}
export function AMCChargesTable({ orderId }) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [accessorialFilter, setAccessorialFilter] = useState('all');
    const [accessorialDrawerOpen, setAccessorialDrawerOpen] = useState(false);
    const [selectedAccessorial, setSelectedAccessorial] = useState(null);
    const [showOptionalColumns, setShowOptionalColumns] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const { allRequests, isLoading: isLoadingRequests, error, legs, refetch: refetchAccessorials, } = useOrderAccessorialRequests(orderId);
    useEffect(() => {
        if (error) {
            toast({
                title: 'Error',
                description: 'Failed to load accessorial requests.',
                variant: 'destructive',
            });
        }
    }, [error, toast]);
    const legMap = useMemo(() => new Map(legs.map((l) => [l._id, l])), [legs]);
    // Filter accessorials
    const filteredAccessorials = useMemo(() => {
        const pendingStatuses = ['pending', 'pending-admin', 'pending-shipper'];
        const approvedStatuses = ['approved', 'accepted'];
        return allRequests.filter((req) => {
            var _a, _b;
            const carrierStatus = (_a = req.carrier_status) !== null && _a !== void 0 ? _a : '';
            const shipperStatus = (_b = req.shipper_status) !== null && _b !== void 0 ? _b : '';
            if (accessorialFilter === 'all')
                return true;
            if (accessorialFilter === 'pending') {
                return (pendingStatuses.includes(carrierStatus) ||
                    pendingStatuses.includes(shipperStatus));
            }
            if (accessorialFilter === 'approved') {
                return (approvedStatuses.includes(carrierStatus) ||
                    approvedStatuses.includes(shipperStatus));
            }
            // declined
            return carrierStatus === 'declined' || shipperStatus === 'declined';
        });
    }, [allRequests, accessorialFilter]);
    // Calculate totals
    const filteredShipperTotal = useMemo(() => filteredAccessorials.reduce((sum, req) => sum + getShipperCost(req), 0), [filteredAccessorials]);
    const filteredCarrierTotal = useMemo(() => filteredAccessorials.reduce((sum, req) => sum + getCarrierCost(req), 0), [filteredAccessorials]);
    const handleAccessorialClick = useCallback((req) => {
        setSelectedAccessorial(req);
        setAccessorialDrawerOpen(true);
    }, []);
    if (isLoadingRequests) {
        return (React.createElement("div", { className: "space-y-2" }, [...Array(3)].map((_, i) => (React.createElement(Skeleton, { key: i, className: "h-12 w-full" })))));
    }
    if (error) {
        return (React.createElement("div", { className: "flex items-center gap-2 text-destructive" },
            React.createElement(AlertCircle, { className: "h-4 w-4" }),
            React.createElement("span", null, "Failed to load accessorial requests")));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "grid gap-3 sm:flex sm:flex-wrap sm:items-center sm:justify-between mb-4" },
            React.createElement("div", { className: "flex items-center gap-2" },
                React.createElement("span", { className: "text-sm text-muted-foreground whitespace-nowrap" }, "Filter:"),
                React.createElement(Select, { value: accessorialFilter, onValueChange: (value) => setAccessorialFilter(value) },
                    React.createElement(SelectTrigger, { className: "w-full sm:w-[140px] h-9" },
                        React.createElement(SelectValue, null)),
                    React.createElement(SelectContent, null,
                        React.createElement(SelectItem, { value: "all" }, "All"),
                        React.createElement(SelectItem, { value: "pending" }, "Pending"),
                        React.createElement(SelectItem, { value: "approved" }, "Approved"),
                        React.createElement(SelectItem, { value: "declined" }, "Declined")))),
            React.createElement(Button, { variant: "outline", size: "sm", className: "w-full sm:w-auto border-zinc-300 text-zinc-700 hover:bg-zinc-50 bg-transparent whitespace-nowrap", onClick: (e) => {
                    e.stopPropagation();
                    setShowBulkModal(true);
                } }, "Add Bulk Accessorials")),
        React.createElement("div", { className: "overflow-x-auto" },
            React.createElement(Table, null,
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableHead, { className: "whitespace-nowrap min-w-[160px]" }, "Accessorial"),
                        React.createElement(TableHead, { className: "text-right whitespace-nowrap" }, "Shipper Cost"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Shipper Status"),
                        React.createElement(TableHead, { className: "text-right whitespace-nowrap" }, "Carrier/Vendor Cost"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Carrier Status"),
                        React.createElement(TableHead, { className: "whitespace-nowrap min-w-[120px]" }, "Shipment"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Leg Type"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Equipment"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Missing Documents?"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Action Required"),
                        showOptionalColumns && (React.createElement(React.Fragment, null,
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "Shipper Source"),
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "Carrier Source"),
                            React.createElement(TableHead, { className: "whitespace-nowrap min-w-[150px]" }, "Name"),
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "Created By"),
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "Updated"))),
                        React.createElement(TableHead, { className: "w-12" },
                            React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0", onClick: () => setShowOptionalColumns(!showOptionalColumns), "aria-label": showOptionalColumns
                                    ? 'Hide optional columns'
                                    : 'Show optional columns', title: showOptionalColumns
                                    ? 'Hide optional columns'
                                    : 'Show optional columns' },
                                React.createElement(ChevronRight, { className: cn('h-4 w-4 transition-transform', showOptionalColumns && 'rotate-90') }))))),
                React.createElement(TableBody, null,
                    filteredAccessorials.length > 0 ? (filteredAccessorials.map((req) => {
                        var _a, _b, _c, _d, _e;
                        const shipmentId = getShipmentId(req);
                        const shipmentDisplayId = getShipmentDisplayId(req) ||
                            ((_a = legMap.get(shipmentId)) === null || _a === void 0 ? void 0 : _a.shipment_id) ||
                            '';
                        return (React.createElement(TableRow, { key: req._id },
                            React.createElement(TableCell, null,
                                React.createElement("div", { className: "flex items-center gap-2" },
                                    React.createElement("button", { onClick: () => handleAccessorialClick(req), className: "text-blue-600 hover:underline" }, t(sluggify(req.message), req.message) || NO_DATA),
                                    !((_b = req.equipment_id) === null || _b === void 0 ? void 0 : _b[0]) && React.createElement(MissingEquipmentWarning, null))),
                            React.createElement(TableCell, { className: "text-right" }, formatMoney(getShipperCost(req))),
                            React.createElement(TableCell, null, req.shipper_status ? (React.createElement(AccessorialRequestStatusBadge, { status: req.shipper_status })) : (NO_DATA)),
                            React.createElement(TableCell, { className: "text-right" }, formatMoney(getCarrierCost(req))),
                            React.createElement(TableCell, null, req.carrier_status ? (React.createElement(AccessorialRequestStatusBadge, { status: req.carrier_status })) : (NO_DATA)),
                            React.createElement(TableCell, null, shipmentId ? (React.createElement(ShipmentExternalLink, { id: shipmentId, label: shipmentDisplayId || shipmentId })) : (React.createElement("span", { className: "text-muted-foreground" }, "-"))),
                            React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_c = legMap.get(shipmentId)) === null || _c === void 0 ? void 0 : _c.leg_type) ? (React.createElement(LegTypeBadge, { legType: legMap.get(shipmentId).leg_type })) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                            React.createElement(TableCell, null, (_e = (_d = req.equipment_id) === null || _d === void 0 ? void 0 : _d[0]) !== null && _e !== void 0 ? _e : NO_DATA),
                            React.createElement(TableCell, null, req.missing_docs && (React.createElement("div", { className: "flex items-center gap-1 text-red-700" },
                                React.createElement(AlertCircle, { className: "h-4 w-4 flex-shrink-0" }),
                                React.createElement("span", { className: "text-xs" }, "Missing")))),
                            React.createElement(TableCell, { className: "whitespace-nowrap" }, getActionRequired(req)
                                ? titleize(getActionRequired(req))
                                : NO_DATA),
                            showOptionalColumns && (React.createElement(React.Fragment, null,
                                React.createElement(TableCell, null, titleize(req.receivable_rate_source) || NO_DATA),
                                React.createElement(TableCell, null, titleize(req.payable_rate_source) || NO_DATA),
                                React.createElement(TableCell, null, getPayeeName(req) || NO_DATA),
                                React.createElement(TableCell, null, req.created_by || NO_DATA),
                                React.createElement(TableCell, { className: "whitespace-nowrap" }, formatUpdatedAt(req.modified_at || req.created_at)))),
                            React.createElement(TableCell, null)));
                    })) : (React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: showOptionalColumns ? 16 : 11, className: "text-muted-foreground" }, allRequests.length === 0
                            ? 'No accessorials found'
                            : 'No accessorials match the selected filter'))),
                    filteredAccessorials.length > 0 && (React.createElement(TableRow, { className: "bg-muted/50 font-semibold" },
                        React.createElement(TableCell, null, "Total"),
                        React.createElement(TableCell, { className: "text-right" }, formatMoney(filteredShipperTotal)),
                        React.createElement(TableCell, { className: "text-right" }, formatMoney(filteredCarrierTotal)),
                        React.createElement(TableCell, { colSpan: showOptionalColumns ? 13 : 8 })))))),
        showBulkModal && (React.createElement(BulkAddAccessorialsModal, { show: showBulkModal, orderId: orderId, shipmentIds: legs.map((l) => l._id), onHide: () => {
                setShowBulkModal(false);
                refetchAccessorials();
            }, edit: false })),
        React.createElement(CargomaticPane, { show: accessorialDrawerOpen, onHide: () => {
                setAccessorialDrawerOpen(false);
                setSelectedAccessorial(null);
            }, renderBody: () => accessorialDrawerOpen && (selectedAccessorial === null || selectedAccessorial === void 0 ? void 0 : selectedAccessorial._id) ? (React.createElement(AccessorialRequestPane, { accessorial: selectedAccessorial._id, shipment: getShipmentId(selectedAccessorial), onSave: refetchAccessorials })) : null })));
}
export default AMCChargesTable;
